import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Checkbox } from "../ui/Checkbox"; // Custom Radix-based checkbox

/**
 * RHFCheckbox Component
 * Controlled checkbox integrated with React Hook Form
 * 
 * @param {string} name - The name of the field (used in form state and validation)
 * @param {string} label - Label displayed next to the checkbox
 * @param {string} [className] - Wrapper div custom classes
 * @param {string} [checkboxClass] - Custom classes for the checkbox component
 * @param {boolean} [required=false] - Whether the field is required (shows * indicator)
 * @param {object} [props] - Additional props passed to the Checkbox component
 *
 * @returns {JSX.Element} Controlled checkbox field connected to React Hook Form
 */
const RHFCheckbox = ({
    name,
    label,
    className = "",
    checkboxClass = "",
    required = false,
    ...props
}) => {
    const { control, formState, getFieldState } = useFormContext();

    // Get validation error state for this field
    const { error } = getFieldState(name, formState);

    return (
        <div className={className}>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    // Label wraps checkbox + text for better UX
                    <label
                        htmlFor={name}
                        className="flex items-center gap-2 cursor-pointer select-none"
                    >
                        {/* 
                            Custom Checkbox (Radix-based)
                            - `checked` binds RHF value
                            - `onCheckedChange` updates RHF state
                        */}
                        <Checkbox
                            id={name}
                            checked={field.value || false} // Ensure boolean value
                            onCheckedChange={(checked) =>
                                field.onChange(checked)
                            }
                            className={cn(checkboxClass)}
                            {...props}
                        />

                        {/* Label text */}
                        {label && (
                            <span className="text-sm text-text-secondary">
                                {label}

                                {/* Required indicator */}
                                {required && (
                                    <span className="text-red-400 ml-1">
                                        *
                                    </span>
                                )}
                            </span>
                        )}
                    </label>
                )}
            />

            {/* Error message display */}
            {error && (
                <p className="text-red-400 text-xs mt-1">
                    {error.message}
                </p>
            )}
        </div>
    );
};

export default RHFCheckbox;